<template>
  <div id="presentationSettings">
    <div class="container">

      <div class="page-title">
        <span>{{ $t("companies.presentation.title") }}</span>
        <button class="save btn btn-primary" @click="savePresentation"
          v-if="$store.getters.loggedUserHasModifyPermission">{{ $t("save") }}</button>
      </div>

      <Tabs>
        <TabSection :title="$t('branch.general')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label for="txtAddressAddress">{{ $t('address.address') }}</label>
                <input id="txtAddressAddress" type="text" v-model="address.address"
                  :class="{ invalid: $validate.error('address.address') }">
              </div>
              <div class="group">
                <label for="txtAddressCity">{{ $t('address.city') }}</label>
                <input id="txtAddressCity" type="text" v-model="address.city"
                  :class="{ invalid: $validate.error('address.city') }">
              </div>
              <div class="group">
                <label for="txtAddressCountry">{{ $t('address.country') }}</label>
                <input id="txtAddressCountry" type="text" v-model="address.country"
                  :class="{ invalid: $validate.error('address.country') }">
              </div>
              <div class="group">
                <label for="txtAddressState">{{ $t('address.state') }}</label>
                <input id="txtAddressState" type="text" v-model="address.state"
                  :class="{ invalid: $validate.error('address.state') }">
              </div>
              <div class="group">
                <label for="txtAddressZipcode">{{ $t('address.zipCode') }}</label>
                <input id="txtAddressZipcode" type="text" v-model="address.zipCode"
                  :class="{ invalid: $validate.error('address.zipCode') }">
              </div>
              <div class="group">
                <label for="txtLat">{{ $t('address.latitude') }}</label>
                <input id="txtLat" type="text" v-model="address.latitude"
                  :class="{ invalid: $validate.error('address.latitude') }">
              </div>
              <div class="group">
                <label for="txtLng">{{ $t('address.longitude') }}</label>
                <input id="txtLng" type="text" v-model="address.longitude"
                  :class="{ invalid: $validate.error('address.longitude') }">
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>
                  <span>{{ $t("branch.download_image") }} (400 x 600 px)</span>
                  <Tooltip :text="$t('branch.download_tooltip')"></Tooltip>
                </label>
                <div class="input-group">
                  <input id="uploadImage" ref="uploadImage" @change="uploadedImage($event)" type="file" accept="image/*">
                  <input class="file-upload" type="text" v-model="imageFile" readonly>
                  <button class="btn btn-primary" @click="$refs.uploadImage.click()">{{ $t("branch.download") }}</button>
                </div>
              </div>
              <div class="group">
                <label>{{ $t('branch.preview') }}</label>
                <div class="image-preview" :style="{ backgroundImage: imagePath }"></div>
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('companies.presentation.restaurant')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <LocalizedInput textarea :max-size="200" :title="$t('companies.presentation.description_short')"
                  :translate="information.description.short" />
              </div>
              <div class="group">
                <LocalizedInput :title="$t('companies.presentation.type')" :translate="information.productType" />
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('companies.presentation.price_range') }}</label>
                <input type="text" v-model="information.price_range">
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('companies.presentation.about')">
          <div class="group">
            <LocalizedInput :title="$t('companies.presentation.description_long_title')"
              :translate="information.description.longTitle" />
          </div>
          <div class="group">
            <LocalizedInput textarea :title="$t('companies.presentation.description_long')"
              :translate="information.description.long" />
          </div>
        </TabSection>

        <TabSection :title="$t('companies.presentation.contact_information')">
          <div class="columns">

            <div class="column">
              <div class="subtitle">
                <span class="flex-grow">{{ $t("open_hours") }}</span>
              </div>
              <div class="warning">
                {{ $t('companies.presentation.warning') }}
                <router-link :to="{ path: '/settings/services/online-ordering', query: $route.query }" class="sub-link"
                  @click.native="$emit('close-menu')">
                  Services.
                </router-link>
              </div>
              <DayTimeRange v-for="(day, dayIndex) in information.hours" :key="dayIndex" :day="day" :dayName="day.name"
                :validate="$validate" :errorsKey="'information.hours[' + dayIndex + '].hours'"></DayTimeRange>
            </div>

            <div class="column">
              <div class="subtitle">
                <span class="flex-grow">{{ $t("companies.presentation.social_networks") }}</span>
              </div>
              <div class="group">
                <label>Facebook</label>
                <input type="text" v-model="information.social.facebook">
              </div>
              <div class="group">
                <label>Instagram</label>
                <input type="text" v-model="information.social.instagram">
              </div>
              <div class="group">
                <label>Twitter</label>
                <input type="text" v-model="information.social.twitter">
              </div>
              <div class="group">
                <label>{{ $t("companies.presentation.web_site") }}</label>
                <input type="text" v-model="information.social.webSite">
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('companies.presentation.contacts_method')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('companies.presentation.email') }}</label>
                <input type="text" v-model="email">
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('companies.presentation.phoneNumber') }}</label>
                <input type="text" v-model="phoneNumber">
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('companies.presentation.custom_links')">
          <div class="customLinkAdd">
            <button class="btn btn-primary" @click="addCustomLink" v-if="$store.getters.loggedUserHasModifyPermission">{{
              $t('branch.add') }}</button>
          </div>
          <div v-for="(link, linkIndex) of information.customMenuExternalLinks" :key="link.url + '-' + linkIndex">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <LocalizedInput title="Titre" :translate="link.title" />
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('companies.presentation.link') }}</label>
                  <input type="text" v-model="link.url">
                </div>
              </div>
              <div class="column customLinkRemove" v-if="$store.getters.loggedUserHasModifyPermission">
                <button class="btn btn-primary" @click="removeCustomLink(linkIndex)">X</button>
              </div>
            </div>
          </div>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserHasModifyPermission">
        <button class="btn btn-primary" @click="savePresentation">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import DayTimeRange from '@/components/tools/DayTimeRange.vue';
  import { mapActions } from 'vuex';
  import Tooltip from "@/components/tools/Tooltip.vue";
  import LocalizedInput from "@/components/tools/LocalizedInput";

  export default Vue.extend({

    name: 'PresentationSettings',

    components: { DayTimeRange, Tooltip, LocalizedInput },

    validate: {
      "address.address": {
        required: true
      },
      "address.city": {
        required: true
      },
      "address.country": {
        required: true
      },
      "address.state": {
        required: true
      },
      "address.zipCode": {
        required: true
      },
      "address.latitude": {
        required: true,
        number: true
      },
      "address.longitude": {
        required: true,
        number: true
      },
      "information.hours": {
        array: true, elements: {
          hours: { array: true, elements: { from: { required: true, hour: true }, to: { required: true, hour: true } } }
        }
      },
      "information.description.short.fr": {
        maxLength: 200
      },
      "information.description.short.en": {
        maxLength: 200
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "companies.settings.presentation";
      this.$store.state.pageIcon = "/icon/branches.svg";
      this.load();

      if (!this.$store.getters.loggedUserHasModifyPermission) {
        window.lockFields();
      }
    },

    data() {
      return {
        languages: null,
        image: { url: "" },
        imageFile: null,
        address: {
          address: "",
          city: "",
          country: "",
          state: "",
          zipCode: "",
          latitude: "",
          longitude: "",
        },
        information: {
          description: {
            short: {}, /* Translation */
            long: {}, /* Translation */
            longTitle: {} /* Translation */
          },
          productType: {}, /* Translation */
          price_range: '',
          social: {
            facebook: '',
            twitter: '',
            instagram: '',
            webSite: ''
          },
          hours: this.emptyHours(),
          customMenuExternalLinks: [], /* Translation[] */
        },
        phoneNumber: '',
        email: ''
      }
    },

    computed: {
      imagePath() {
        if (this.image && this.image.url) {
          let path = this.image.url.startsWith("data:") ? this.image.url : Parameters.urlFileServer + this.image.url;
          return "url('" + path + "')";
        } else {
          return "url('/img/photo-placeholder.jpg')";
        }
      }
    },

    methods: {

      ...mapActions(["getCompany", "saveCompany"]),

      emptyHours() {
        return [
          { name: "monday", open: false, hours: [] },
          { name: "tuesday", open: false, hours: [] },
          { name: "wednesday", open: false, hours: [] },
          { name: "thursday", open: false, hours: [] },
          { name: "friday", open: false, hours: [] },
          { name: "saturday", open: false, hours: [] },
          { name: "sunday", open: false, hours: [] }
        ]
      },

      async load() {
        this.$store.commit("showSpinner", true);
        let response = await this.getCompany({
          branchId: this.$route.query.branch,
          companyId: this.$route.query.company,
          fallback: false
        });
        this.$store.commit("showSpinner", false);

        if (response) {
          if (response.information) {
            this.information = response.information;
            this.information.description.short = {};
            this.information.description.long = {};
            this.information.description.longTitle = {};
          }
          if (!this.information.hours || this.information.hours.length === 0) {
            this.information.hours = this.emptyHours();
          }
          if (response.address) {
            this.address = response.address;
          }
          this.image = response.image;
          this.email = response.email;
          this.phoneNumber = response.phoneNumber;
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      async savePresentation() {
        console.log(this.$validate.data());
        if (!this.$validate.valid()) {
          toast(this.$t("error.invalid_fields"), "error");
          return;
        }
        this.$store.commit("showSpinner", true);
        let response = await this.saveCompany({
          branchId: this.$route.query.branch,
          companyId: this.$route.query.company,
          data: {
            image: (this.imageFile ? this.getBase64Data(this.image.url) : undefined),
            information: this.information,
            address: this.address,
            phoneNumber: this.phoneNumber,
            email: this.email
          }
        });
        if (response) {
          toast(this.$t("branch.success"), "success");
        } else {
          toast(this.$t("error.no_server"), "error");
        }
        this.$store.commit("showSpinner", false);
      },

      uploadedImage(e) {
        let file = e.target.files[0];
        if (!["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
          this.$modal.show("file-type-image-error");
        } else if (file.size > window.MAX_FILE_SIZE) {
          this.$modal.show("file-size-error");
        } else {
          this.imageFile = e.target.value.split('\\').pop();
          let fileReader = new FileReader();
          fileReader.onload = () => {
            this.image = this.image || { url: "" };
            this.image.url = fileReader.result;
          };
          fileReader.readAsDataURL(file);
        }
      },

      getBase64Data(url) {
        return url.replace(/^data:image\/(png|jpeg|gif);base64,/, "");
      },

      addCustomLink() {
        this.information.customMenuExternalLinks.push({
          title: { fr: "", en: "" },
          url: ""
        });
      },

      removeCustomLink(index) {
        this.information.customMenuExternalLinks.splice(index, 1);
      }

    }

  });
</script>

<style lang="scss" scoped>
  #presentationSettings {
    display: flex;
    flex-direction: column;
  }

  .image-preview {
    display: flex;
    height: 260px;
    border-radius: 5px;
    border: 1px solid #d9d9d9;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }

  #uploadImage {
    position: absolute;
    left: -999999px;
  }

  .customLinkAdd {
    text-align: right;
    margin-bottom: 10px;
  }

  .customLinkRemove {
    flex-grow: 0;
    display: flex;
    align-items: center;
  }

  .warning {
    color: red;
    font-weight: bold;
    margin-bottom: 16px;
  }
</style>